import { useCallback, useEffect, useMemo, useState } from "react";
import { IVehicle } from "@/types/vehicle.type";
import { getIn, useFormikContext } from "formik";
import SelectFormik from "@/common/components/form/SelectFormik";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { CheckboxField } from "@/modules/renters/components/modals/fields/EditBondRenterVehcile/formsModal/field/CheckboxField";
import RegisterPhotos from "@/modules/renters/components/modals/fields/EditBondRenterVehcile/formsModal/field/RegisterPhotos";
import { partsEvaluationData } from "@/common/helpers/partsEvaluationData";


type InspectionFormValues = {
  vehicleId: string;
  selectedLicensePlate: string;
  registers: Array<{ type: string; content: string; verified?: boolean }>;
  optionRepossesion: string;
};
type Props = {
  submitButtonText: string;
  isLoading: boolean;
  vehicles: IVehicle[];
};

export default function VehicleInspectionFormBody({
  submitButtonText,
  isLoading,
  vehicles,
}: Props) {
  const formik = useFormikContext<InspectionFormValues>();
  const { values, setFieldValue } = formik;
  const registers = getIn(formik.values, "registers");
  const optionRepossesion = getIn(formik.values, "optionRepossesion");
  const vehicleOptionsAsStrings = vehicles.map(
    (vehicle) => vehicle.licensePlate
  );
  const [disableButton, setDisableButton] = useState(true);
  useEffect(() => {
    if (optionRepossesion !== "") {
      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
  }, [optionRepossesion]);

  const onAddRegister = () => {
    formik.setFieldValue("registers", [
      ...registers,
      { type: "", content: "", verified: false },
    ]);
  };

  const handleVehicleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement> | { target: { value: string; } }) => {
    const selectedPlate = e.target.value;
    const currentPlate = values.selectedLicensePlate;


    if (!selectedPlate || selectedPlate === "") {
      setFieldValue("selectedLicensePlate", "");
      setFieldValue("vehicleId", "");
      setFieldValue("registers", []);
      return;
    }

    if (selectedPlate !== currentPlate) {
      setFieldValue("selectedLicensePlate", selectedPlate);

      const selectedVehicle = vehicles.find(
        (v) => v.licensePlate === selectedPlate
      );

      if (selectedVehicle) {
        setFieldValue("vehicleId", selectedVehicle._id);

        // Reset dos registros
        const initialRegisters = [
          { type: "Frente do veículo", content: "" },
          { type: "Lateral direita do veículo", content: "" },
          { type: "Lateral esquerda do veículo", content: "" },
          { type: "Traseira do veículo", content: "" },
          { type: "Odômetro", content: "" },
        ];
        setFieldValue("registers", initialRegisters);
      }
    }
  }, [values.selectedLicensePlate, vehicles, setFieldValue]);

  const registerPhotosComponent = useMemo(() => {
    // Se não há placa selecionada, retorna versão desabilitada
    if (!values.selectedLicensePlate) {
      return (
        <div className="opacity-50 pointer-events-none">
          <RegisterPhotos
            key="empty" // Key diferente para forçar remount
            title={"Inspeção de Veículo"}
            items={[]}
            onAddItem={onAddRegister}
            onRemoveItem={onAddRegister}
            titleTextArea={"Descreva o motivo da devolução:"}
            renterId={""}
            nameRenter={""}
            vehicleId={""}
          />
        </div>
      );
    }

    // Componente ativo com dados da placa selecionada
    return (
      <RegisterPhotos
        key={values.selectedLicensePlate} // Key baseada na placa para forçar remount
        title={"Inspeção de Veículo"}
        items={registers}
        onAddItem={onAddRegister}
        onRemoveItem={onAddRegister}
        titleTextArea={"Descreva o motivo da devolução:"}
        renterId={""}
        nameRenter={""}
        vehicleId={values.vehicleId}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.selectedLicensePlate,
    values.vehicleId,
  ]);



  return (
    <section>
      <div className="mb-4">
        <SelectFormik
          label="Placa do Veículo:"
          name="selectedLicensePlate"
          options={vehicleOptionsAsStrings}
          onChange={handleVehicleChange}
          allowFilter={true}
          placeholder="Digite a placa para filtrar..."
        />
      </div>
      {registerPhotosComponent}
      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-4">Avaliação das Peças</h3>
        {partsEvaluationData.map((part) => (
          <CheckboxField
            key={part.name}
            name={`partsEvaluation.${part.name}`}
            label={part.label}
            onChange={() => { }}
          />
        ))}

        <div className="flex justify-end mt-2">
          <ButtonWithLoader
            disabled={disableButton}
            type="submit"
            isLoading={isLoading}
          >
            {submitButtonText}
          </ButtonWithLoader>
        </div>
      </div>
    </section>
  );
}
